import type {
  Agent,
  Artist,
  InternalCommsChannel,
  InternalCommsMessage,
  InternalDmThread,
  Persona,
  StudioId,
} from '@/domain/types';

export interface Person {
  id: string;
  displayName: string;
  role: string;
  studioId?: StudioId;
  avatarUrl?: string;
  initials: string;
}

const SYSTEM_PEOPLE: Person[] = [
  {
    id: 'system-bot',
    displayName: 'Operations Bot',
    role: 'system',
    initials: 'OB',
  },
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Maps the acting session (persona + its id fields) to the comms person id that
 * authors/receives messages on their behalf. Owner falls back to a literal
 * 'owner' id, apprentice falls back to the artist id; every other Ops persona
 * uses the agent id. Returns null when the session has no resolvable id.
 */
export function selfPersonIdFromSession(opts: {
  persona: Persona | null;
  agentId: string | null;
  artistId: string | null;
  apprenticeId: string | null;
}): string | null {
  if (opts.persona === 'owner') return opts.agentId ?? 'owner';
  if (opts.persona === 'artist') return opts.artistId;
  if (opts.persona === 'apprentice') return opts.apprenticeId ?? opts.artistId;
  return opts.agentId;
}

/** Unified pool of every person who can author/receive a comms message. */
export function peoplePool(
  agents: Record<string, Agent>,
  artists: Record<string, Artist>,
): Person[] {
  const out: Person[] = [...SYSTEM_PEOPLE];
  for (const a of Object.values(agents)) {
    const displayName = `${a.firstName} ${a.lastName}`.trim();
    out.push({
      id: a.id,
      displayName,
      role: a.role,
      studioId: a.primaryStudio,
      avatarUrl: a.avatarUrl,
      initials: initialsOf(displayName),
    });
  }
  for (const ar of Object.values(artists)) {
    const displayName = ar.displayName || `${ar.firstName} ${ar.lastName}`.trim();
    out.push({
      id: ar.id,
      displayName,
      role: ar.isApprentice ? 'apprentice' : 'artist',
      studioId: ar.studioId,
      avatarUrl: ar.avatarUrl,
      initials: initialsOf(displayName),
    });
  }
  return out;
}

export function personById(pool: Person[], id: string): Person | undefined {
  return pool.find((p) => p.id === id);
}

/** Channels the current persona is allowed to see (scope-filtered). */
export function channelsForPersona(
  persona: Persona | null,
  agentId: string | null,
  channels: Record<string, InternalCommsChannel>,
): InternalCommsChannel[] {
  if (!persona) return [];
  const all = Object.values(channels);
  return all.filter((c) => {
    if (persona === 'owner') return true;
    if (persona === 'georgina') return c.scope !== 'studio-apprentices' || true;
    if (persona === 'studio-manager') {
      return c.scope === 'network' || c.scope === 'studio-ops' || c.scope === 'studio-artists' || c.scope === 'studio-apprentices' || c.scope === 'managers';
    }
    if (persona === 'bookings-agent') {
      if (!agentId) return c.scope === 'network';
      return c.scope === 'network' || (c.scope === 'studio-ops' && c.memberIds.includes(agentId));
    }
    if (persona === 'artist') {
      return c.scope === 'network' || c.scope === 'studio-artists' || c.scope === 'studio-ops';
    }
    if (persona === 'apprentice') {
      return c.scope === 'studio-apprentices' || c.scope === 'studio-artists';
    }
    return c.scope === 'network';
  });
}

/** DM threads the persona is a participant in. */
export function dmsForPersona(
  personId: string | null,
  threads: Record<string, InternalDmThread>,
): InternalDmThread[] {
  if (!personId) return [];
  return Object.values(threads).filter((t) => t.participantIds.includes(personId));
}

/** Most-recent message in a channel or DM thread (for sort + preview). */
export function lastMessageOf(
  container: InternalCommsChannel | InternalDmThread,
): InternalCommsMessage | undefined {
  const msgs = container.messages;
  return msgs.length > 0 ? msgs[msgs.length - 1] : undefined;
}

/** Unread count for a single channel/DM relative to a lastReadIso. */
export function unreadInContainer(
  container: InternalCommsChannel | InternalDmThread,
  lastReadIso: string | undefined,
  selfPersonId: string | null,
): number {
  if (!lastReadIso) {
    // Never opened — count every message not authored by self.
    return container.messages.filter((m) => m.authorId !== selfPersonId).length;
  }
  return container.messages.filter(
    (m) => m.authorId !== selfPersonId && m.timestamp > lastReadIso,
  ).length;
}

/** Network-wide unread comms count for the badge. */
export function unreadCommsCount(opts: {
  persona: Persona | null;
  agentId: string | null;
  selfPersonId: string | null;
  channels: Record<string, InternalCommsChannel>;
  dms: Record<string, InternalDmThread>;
  lastReadByChannel: Record<string, string>;
}): number {
  const channels = channelsForPersona(opts.persona, opts.agentId, opts.channels);
  const dms = dmsForPersona(opts.selfPersonId, opts.dms);
  let total = 0;
  for (const c of channels) {
    total += unreadInContainer(c, opts.lastReadByChannel[c.id], opts.selfPersonId);
  }
  for (const d of dms) {
    total += unreadInContainer(d, opts.lastReadByChannel[d.id], opts.selfPersonId);
  }
  return total;
}

/** Choose a target channel id for an auto-ping based on event + studio. */
export function autoPingChannelFor(
  // Retained for call-site clarity; routing no longer varies by event type.
  _eventType:
    | 'deposit-paid'
    | 'booking-created'
    // G5: threshold-breach alerts route like deposit-paid (studio ops, then network).
    | 'threshold-breach',
  studioId: StudioId | undefined,
  channels: Record<string, InternalCommsChannel>,
): string | undefined {
  // deposit-paid + booking-created + threshold-breach prefer the studio's *-ops channel.
  if (studioId) {
    const opsId = `${studioId}-ops`;
    if (channels[opsId]) return opsId;
  }
  return channels.network ? 'network' : undefined;
}
